import { useState, useEffect } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  User, Bell, Shield, Users, Lock, Zap, CreditCard, Database, Key,
  Settings as SettingsIcon, ChevronRight, Check, Save, Plus, Edit3,
  Trash2, LogOut, Mail, Phone, Clock, X, AlertTriangle, RefreshCw, Sparkles, Activity,
  GraduationCap, ShieldAlert, KeyRound, Copy, ListChecks
} from "lucide-react";
import FieldOptionsPanel from "@/components/settings/FieldOptionsPanel";
import EhiDocCategoriesPanel from "@/components/settings/EhiDocCategoriesPanel";
import { ROLES, PERMISSIONS, canPerform } from "@/lib/permissions";
import DeliveryStatusBadge from "@/components/DeliveryStatusBadge";
import AdminOps, { ADMIN_OPS_SECTIONS } from "./AdminOps";
import TrainingAdminPanel from "@/components/training/TrainingAdminPanel";
import RbacMatrix from "@/components/RbacMatrix";
import RolePreview from "@/components/RolePreview";
import UserPermissionEditor from "@/components/UserPermissionEditor";
import ScorerConfigEditor from "@/components/ScorerConfigEditor";import AuditLogViewer from "@/components/AuditLogViewer";
import TeamManagement from "@/components/TeamManagement";
import MySignature from "@/components/MySignature";
import SignatureManager from "@/components/SignatureManager";
import SamplePipelinePanel from "@/components/SamplePipelinePanel";
import RCIntegrationPanel from "@/components/RCIntegrationPanel";
import IssueReportsAdmin from "@/components/IssueReportsAdmin";
import IssueReportsMine from "@/components/IssueReportsMine";
import PipelineSettingsPanel from "@/components/PipelineSettingsPanel";
import DataAdminTools from "@/components/DataAdminTools";
import ActivityLogViewer from "@/components/ActivityLogViewer";
import AISummaryPipelinesPanel from "@/components/AISummaryPipelinesPanel";

// Team-member Flag: an optional secondary functional label, independent of
// Role (grants no permissions). Must stay in sync with backend USER_FLAGS.
const USER_FLAGS = [
  "Sales Manager",
  "Subject matter expert",
  "AR representative",
  "Sales Caller",
  "Client service representative",
  "Client retention manager",
  "Support Manager",
  "MIPS expert",
  "Support representative",
  "Sales Associates",
  "Client service manager",
  "Program manager",
  "RCM manager",
];
const FLAG_PILL = { fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: "#EEF2FF", color: "#3730A3" };

// Team-member Product: the assigned appointment-setting product line.
// Optional; independent of Role and Title/Flag. Must stay in sync with
// backend USER_PRODUCTS.
const USER_PRODUCTS = [
  "CCM Appt Setter",
  "RCM Appt Setter",
];
const PRODUCT_PILL = { fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: "#ECFEFF", color: "#155E75" };

const SETTINGS_SECTIONS = {
  account: {
    label: "Account",
    items: [
      { key: "profile", label: "My Profile", icon: User, roles: ["super_admin","crm_admin","sales_agent","sales_manager","marketing_manager","marketing_agent","read_only"] },
      { key: "my-signature", label: "My Signature", icon: Mail, roles: ["super_admin","crm_admin","sales_agent","sales_manager","marketing_manager","marketing_agent","read_only"] },
      { key: "signature-manager", label: "Signature Manager", icon: Mail, roles: ["super_admin"] },
      { key: "notifications", label: "Notifications", icon: Bell, roles: ["super_admin","crm_admin","sales_agent","sales_manager","marketing_manager","marketing_agent","read_only"] },
      { key: "security", label: "Security", icon: Lock, roles: ["super_admin","crm_admin","sales_agent","sales_manager","marketing_manager","marketing_agent","read_only"] },
    ],
  },
  team: {
    label: "Users & Access",
    items: [
      { key: "members", label: "Team Members", icon: Users, roles: ["super_admin"] },
      { key: "teams", label: "Teams", icon: Users, roles: ["super_admin","crm_admin","sales_manager"] },
      { key: "roles", label: "Roles & Permissions", icon: Shield, roles: ["super_admin"] },
      { key: "activity", label: "Activity Log", icon: Clock, roles: ["super_admin"] },
      { key: "audit", label: "Security & Audit", icon: ShieldAlert, roles: ["super_admin","crm_admin"] },
    ],
  },
  integrations: {
    label: "Integrations",
    items: [
      // Iter 142.2 issue #02101df6 — reps (tatiana, ioannis) reported "no
      // option to connect Microsoft email anymore". The section was gated
      // to admins. Reps need this surface to (re)connect their per-rep
      // M365 token + view their RingCentral status. The page itself
      // hides tenant-level admin controls behind role guards already.
      { key: "integrations", label: "Connected Services", icon: Zap, roles: ["super_admin", "crm_admin", "sales_manager", "sales_agent", "demo_agent", "senior_sales", "sdr", "call_agent", "bookings_agent", "marketing_manager", "marketing_agent"] },
    ],
  },
  training_admin: {
    label: "Training",
    items: [
      { key: "training", label: "Training & Onboarding", icon: GraduationCap, roles: ["super_admin","crm_admin"] },
    ],
  },
  system: {
    label: "System",
    items: [
      { key: "billing", label: "Billing & Plan", icon: CreditCard, roles: ["super_admin"] },
      { key: "data", label: "Data & Storage", icon: Database, roles: ["super_admin"] },
      { key: "sample_data", label: "Sample Pipeline", icon: Sparkles, roles: ["super_admin","crm_admin"] },
      { key: "ai_targeting", label: "AI Targeting · Weights", icon: Sparkles, roles: ["super_admin","crm_admin"] },
      { key: "pipeline_settings", label: "Pipeline Thresholds", icon: Zap, roles: ["super_admin","crm_admin"] },
      { key: "field-options", label: "Field Options", icon: ListChecks, roles: ["super_admin"] },
      { key: "ehi-doc-categories", label: "EHI Document Categories", icon: ListChecks, roles: ["super_admin"] },
      { key: "automations", label: "Automations", icon: Zap, roles: ["super_admin"] },
      { key: "ai_summary_pipelines", label: "AI Summary Pipelines", icon: Sparkles, roles: ["super_admin"] },
      { key: "issue_reports", label: "Issue Reports", icon: AlertTriangle },
    ],
  },
  admin_ops: {
    label: "Admin Ops",
    items: ADMIN_OPS_SECTIONS,
  },
};

export default function Settings() {
  const { user, token, refreshUser } = useAuth();
  const role = user?.role || "super_admin";
  const [activeSection, setActiveSection] = useState(() => {
    try {
      const s = new URLSearchParams(window.location.search).get("section");
      // Settings IA cleanup (Feb 2026): the old Admin Ops "Team & Roles"
      // pane (ops_users) and the legacy "role" tab key are gone — both
      // flows now live inside "Team Members". Silently redirect any
      // surviving deep-links/bookmarks so refresh + back/forward Just Work.
      const REDIRECTS = { ops_users: "members", role: "members" };
      if (s && REDIRECTS[s]) {
        const target = REDIRECTS[s];
        try {
          const u = new URL(window.location.href);
          u.searchParams.set("section", target);
          window.history.replaceState({}, "", u.toString());
        } catch { /* history api unavailable — no-op */ }
        return target;
      }
      return s || "profile";
    } catch { return "profile"; }
  });
  // Iter 145 — persist active section in the URL so reload + back/forward
  // restore the rep's place. Replace-state (not push) to avoid history
  // spam as managers click through several sections in a row.
  useEffect(() => {
    try {
      const u = new URL(window.location.href);
      if (u.searchParams.get("section") !== activeSection) {
        u.searchParams.set("section", activeSection);
        window.history.replaceState({}, "", u.toString());
      }
    } catch { /* history api unavailable — no-op */ }
  }, [activeSection]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamTab, setTeamTab] = useState("active"); // active | disabled — view-only split
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteData, setInviteData] = useState({ name: "", email: "", role: "sales_agent", flag: "", product: "" });
  const [productFilter, setProductFilter] = useState(""); // "" = all · "__none__" = no product
  const [selectedRole, setSelectedRole] = useState("super_admin");
  const [roleEditingId, setRoleEditingId] = useState(null);
  // Per-user permission override modal (Phase D).
  const [permEditUser, setPermEditUser] = useState(null);
  // Iter 165 — Admin "Issue password reset URL" modal state.
  const [resetUrlUser, setResetUrlUser] = useState(null);
  const [resetUrlBusy, setResetUrlBusy] = useState(false);
  const [resetUrlResult, setResetUrlResult] = useState(null);
  const [resetUrlReason, setResetUrlReason] = useState("");
  const [resetUrlClearLockout, setResetUrlClearLockout] = useState(true);

  // Real-mode Team Members: fetch from /api/admin/users.
  // Gated by super_admin (endpoint returns 403 otherwise). We render
  // whatever the API returns — no local fallback to DEMO_USERS in prod.
  const fetchTeamMembers = async () => {
    if (!token) return;
    setTeamLoading(true); setTeamError("");
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      // Normalize to the shape the UI expects (avatar + lastActive). Server
      // provides id/full_name/email/role/status/last_login_at.
      const rows = (res.data || []).map(u => ({
        ...u,
        avatar: (u.full_name || u.email || "?").split(/\s+/).map(s => s[0]).slice(0, 2).join("").toUpperCase(),
        lastActive: u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "Never",
      }));
      setTeamMembers(rows);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        // Non-super-admin viewer — show empty table rather than a scary error.
        setTeamMembers([]);
      } else {
        setTeamError(err?.response?.data?.detail || err?.message || "Failed to load team members");
      }
    } finally {
      setTeamLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchTeamMembers defined above; refetch only when auth token changes
  useEffect(() => { fetchTeamMembers(); }, [token]);

  // Always default to the Active tab whenever Team Members is (re)opened.
  useEffect(() => { if (activeSection === "members") setTeamTab("active"); }, [activeSection]);

  // Team Members Active/Disabled split (view-only). Anything not explicitly
  // "disabled" (incl. "invited" / unknown) counts as Active so no one hides.
  const teamActive = teamMembers.filter(m => m.status !== "disabled");
  const teamDisabled = teamMembers.filter(m => m.status === "disabled");
  const teamShownBase = teamTab === "disabled" ? teamDisabled : teamActive;
  const teamShown = productFilter
    ? teamShownBase.filter(m => (productFilter === "__none__" ? !m.product : m.product === productFilter))
    : teamShownBase;

  // Profile form — hydrated from the live user object so logout/login
  // round-trips show the saved values.
  const [profileName, setProfileName] = useState(user?.full_name || "");
  const [profileTitle, setProfileTitle] = useState(user?.title || "");
  const [profilePhone, setProfilePhone] = useState(user?.phone || "");
  const [profileTimezone, setProfileTimezone] = useState(user?.timezone || "America/New_York");
  const [profileSaving, setProfileSaving] = useState(false);

  // Re-hydrate when the user object updates (e.g. after refreshUser()).
  // Depend on the whole `user` reference — `react-hooks/exhaustive-deps`
  // is satisfied without listing each scalar field, and re-renders only
  // fire when AuthProvider produces a new user object (login / refresh
  // / logout).
  useEffect(() => {
    if (!user) return;
    setProfileName(user.full_name || "");
    setProfileTitle(user.title || "");
    setProfilePhone(user.phone || "");
    setProfileTimezone(user.timezone || "America/New_York");
  }, [user]);

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      await axios.patch(
        `${API}/users/me`,
        { name: profileName, title: profileTitle, phone: profilePhone, timezone: profileTimezone },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Pull the canonical record back so any other surface (signature
      // preview, top-bar avatar, etc.) updates without a page reload.
      await (refreshUser ? refreshUser() : Promise.resolve());
      toast.success("Profile saved");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Save failed";
      toast.error(`Profile save failed: ${detail}`);
    } finally {
      setProfileSaving(false);
    }
  };

  // Notification toggles
  const [notifs, setNotifs] = useState({
    newLead: true, stageChange: true, commReceived: true, agentDraft: true, demoScheduled: true,
    staleAlerts: true, nextAction: true, campaignSummary: false, dailyDigest: false, weeklySummary: true
  });

  // Iter 329 — Activity Log now sourced live from GET /api/admin/activity-log
  // (see ActivityLogViewer.jsx). The old hardcoded April-2026 demo array
  // was removed here.

  // Invite/create a new team member via the secure invite flow
  // (POST /api/admin/invites). The server returns a single-use invite URL;
  // we surface it in the toast + copy it to clipboard so the admin can share
  // it out-of-band even if SMTP isn't configured.
  const handleInvite = async () => {
    if (!inviteData.name || !inviteData.email) { toast.error("Name and email required"); return; }
    setInviteBusy(true);
    try {
      const res = await axios.post(`${API}/admin/invites`, {
        email: inviteData.email.trim().toLowerCase(),
        role: inviteData.role,
        flag: inviteData.flag || null,
        product: inviteData.product || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const url = res.data?.invite_url;
      const emailed = res.data?.emailed;
      if (url) {
        try { await navigator.clipboard?.writeText(url); } catch {}
        toast.success(
          `Invite sent${emailed ? " (email delivered)" : ""} — link copied to clipboard`,
          { duration: 12000 }
        );
      } else {
        toast.success(`Invited ${inviteData.email}`);
      }
      setShowInvite(false);
      setInviteData({ name: "", email: "", role: "sales_agent", flag: "", product: "" });
      await fetchTeamMembers();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || err?.message;
      if (status === 409) toast.error(`Already exists — ${msg}`);
      else if (status === 403) toast.error(`Not allowed — ${msg}`);
      else if (status === 400) toast.error(`Invalid — ${msg}`);
      else toast.error(`Invite failed — ${msg || "server error"}`);
    } finally {
      setInviteBusy(false);
    }
  };

  // Disable / re-enable a user via the admin status endpoints. The UI
  // updates optimistically; on failure we refetch to snap back to truth.
  const handleToggleStatus = async (member) => {
    const willDisable = member.status !== "disabled";
    const path = willDisable ? "disable" : "enable";
    try {
      await axios.post(`${API}/admin/users/${member.id}/${path}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${member.full_name} ${willDisable ? "disabled" : "re-enabled"}`);
      await fetchTeamMembers();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Status update failed";
      toast.error(msg);
    }
  };

  // Role change via PATCH /api/admin/users/{id}/role. Now goes through a
  // confirmation dialog showing the before/after capability diff (Phase C).
  const [pendingRoleChange, setPendingRoleChange] = useState(null); // { member, newRole }
  const requestRoleChange = (member, newRole) => {
    if (newRole === member.role) { setRoleEditingId(null); return; }
    setPendingRoleChange({ member, newRole });
  };
  const handleRoleChange = async (member, newRole) => {
    if (newRole === member.role) { setRoleEditingId(null); return; }
    try {
      await axios.patch(`${API}/admin/users/${member.id}/role`, { role: newRole }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${member.full_name} is now ${ROLES[newRole]?.label || newRole}`);
      setRoleEditingId(null);
      setPendingRoleChange(null);
      await fetchTeamMembers();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Role change failed";
      toast.error(msg);
    }
  };

  // Flag change via PATCH /api/admin/users/{id}/flag. Independent of role;
  // "" clears the flag. Applied directly (no confirm dialog — no side effects).
  const handleFlagChange = async (member, newFlag) => {
    if ((newFlag || "") === (member.flag || "")) { setRoleEditingId(null); return; }
    try {
      await axios.patch(`${API}/admin/users/${member.id}/flag`, { flag: newFlag || null }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(newFlag ? `${member.full_name} flagged as ${newFlag}` : `Flag cleared for ${member.full_name}`);
      setRoleEditingId(null);
      await fetchTeamMembers();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Flag change failed";
      toast.error(msg);
    }
  };

  // Product change via PATCH /api/admin/users/{id}/product. Independent of
  // role/flag; "" clears the product. Applied directly (no confirm dialog).
  const handleProductChange = async (member, newProduct) => {
    if ((newProduct || "") === (member.product || "")) { setRoleEditingId(null); return; }
    try {
      await axios.patch(`${API}/admin/users/${member.id}/product`, { product: newProduct || null }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(newProduct ? `${member.full_name} assigned to ${newProduct}` : `Product cleared for ${member.full_name}`);
      setRoleEditingId(null);
      await fetchTeamMembers();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Product change failed";
      toast.error(msg);
    }
  };

  // Iter 263 — close the row's inline Role/Flag editor on an OUTSIDE click
  // instead of on the Role select's blur. The old onBlur collapsed the row
  // the instant focus moved from Role → Flag, throwing the flag change away.
  // Skip while a role-change confirm modal is pending (its buttons are
  // outside the row) and while a save is mid-flight.
  useEffect(() => {
    if (!roleEditingId) return;
    const onDown = (e) => {
      if (pendingRoleChange) return;
      const rowEl = document.querySelector(`[data-testid="team-member-${roleEditingId}"]`);
      if (rowEl && rowEl.contains(e.target)) return; // click inside editing row — keep open
      setRoleEditingId(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [roleEditingId, pendingRoleChange]);

  const renderNavSection = (sectionKey, section) => {
    const visible = section.items.filter(i => !i.roles || i.roles.includes(role));
    if (visible.length === 0) return null;
    return (
      <div key={sectionKey} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.5px", padding: "0 12px", marginBottom: 4 }}>{section.label}</div>
        {visible.map(item => {
          const Icon = item.icon;
          return (
            <button key={item.key} onClick={() => setActiveSection(item.key)}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
                border: "none", cursor: "pointer", borderRadius: 6, fontSize: 12, fontWeight: activeSection === item.key ? 600 : 400,
                background: activeSection === item.key ? "var(--blue-light)" : "transparent",
                color: activeSection === item.key ? "var(--blue)" : "var(--text-secondary)",
                borderLeft: activeSection === item.key ? "2px solid var(--blue)" : "2px solid transparent",
                fontFamily: "var(--font)"
              }}
              data-testid={`settings-nav-${item.key}`}>
              <Icon style={{ width: 14, height: 14 }} /> {item.label}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div data-testid="settings-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Account, team, and system configuration</p>
        </div>
      </div>
      <div className="page-content" style={{ display: "flex", gap: 16 }}>
        {/* Left Nav */}
        <div style={{ width: 220, flexShrink: 0 }}>
          {Object.entries(SETTINGS_SECTIONS).map(([k, s]) => renderNavSection(k, s))}
        </div>

        {/* Right Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Profile */}
          {activeSection === "profile" && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-profile">
              <h2 style={{ fontSize: 16, fontWeight: 650, marginBottom: 16 }}>My Profile</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--blue-light)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "var(--blue)" }}>
                  {(user?.full_name || "U").split(" ").map(n => n[0]).join("").toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{user?.full_name}</div>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: ROLES[role]?.pillBg || "var(--gray-light)", color: ROLES[role]?.pillColor || "var(--gray)" }}>
                    {ROLES[role]?.label || role}
                  </span>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Full name</label>
                  <input className="input" value={profileName} onChange={e => setProfileName(e.target.value)} data-testid="profile-name" />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Job title</label>
                  <input className="input" value={profileTitle} onChange={e => setProfileTitle(e.target.value)} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Email</label>
                  <input className="input" value={user?.email || ""} readOnly style={{ background: "var(--gray-light)", color: "var(--text-tertiary)" }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Phone</label>
                  <input className="input" value={profilePhone} onChange={e => setProfilePhone(e.target.value)} placeholder="(555) 000-0000" />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Time zone</label>
                  <select className="input" value={profileTimezone} onChange={e => setProfileTimezone(e.target.value)} style={{ fontSize: 12 }}>
                    <option value="America/New_York">Eastern (ET)</option>
                    <option value="America/Chicago">Central (CT)</option>
                    <option value="America/Denver">Mountain (MT)</option>
                    <option value="America/Los_Angeles">Pacific (PT)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Role</label>
                  <div style={{ padding: "8px 12px", background: "var(--gray-light)", borderRadius: 6 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: ROLES[role]?.pillBg, color: ROLES[role]?.pillColor }}>
                      {ROLES[role]?.label}
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 16, textAlign: "right" }}>
                <button className="btn btn-primary" style={{ fontSize: 12, padding: "8px 18px", opacity: profileSaving ? 0.6 : 1 }} disabled={profileSaving} onClick={saveProfile} data-testid="save-profile">
                  <Save style={{ width: 12, height: 12 }} /> {profileSaving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          )}

          {/* Notifications */}
          {activeSection === "notifications" && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-notifications">
              <h2 style={{ fontSize: 16, fontWeight: 650, marginBottom: 16 }}>Notifications</h2>
              {[
                { section: "Email notifications", items: [
                  { key: "newLead", label: "New lead assigned to me" },
                  { key: "stageChange", label: "Lead stage changed" },
                  { key: "commReceived", label: "Communication received" },
                  { key: "agentDraft", label: "AI agent draft needs approval" },
                  { key: "demoScheduled", label: "Demo scheduled or rescheduled" },
                ]},
                { section: "In-app notifications", items: [
                  { key: "staleAlerts", label: "Stale lead alerts" },
                  { key: "nextAction", label: "Next action reminders" },
                  { key: "campaignSummary", label: "Campaign performance summary" },
                ]},
                { section: "Frequency", items: [
                  { key: "dailyDigest", label: "Daily digest email" },
                  { key: "weeklySummary", label: "Weekly summary email" },
                ]},
              ].map(group => (
                <div key={group.section} style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 12, fontWeight: 620, marginBottom: 8 }}>{group.section}</div>
                  {group.items.map(item => (
                    <div key={item.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
                      <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{item.label}</span>
                      <button onClick={() => setNotifs({ ...notifs, [item.key]: !notifs[item.key] })}
                        style={{ width: 36, height: 20, borderRadius: 10, background: notifs[item.key] ? "var(--blue)" : "var(--gray-light)", border: "none", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
                        <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: notifs[item.key] ? 18 : 2, transition: "left 0.2s" }} />
                      </button>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Security */}
          {activeSection === "security" && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-security">
              <h2 style={{ fontSize: 16, fontWeight: 650, marginBottom: 16 }}>Security</h2>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Current password</label>
                <input className="input" type="password" placeholder="Enter current password" />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>New password</label>
                <input className="input" type="password" placeholder="Enter new password" />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Confirm new password</label>
                <input className="input" type="password" placeholder="Confirm new password" />
              </div>
              <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => toast.success("Password updated (mock)")}>
                Update password
              </button>
            </div>
          )}

          {/* Team Members */}
          {activeSection === "members" && canPerform("settings.team.view", role) && (
            <div className="card" style={{ padding: "20px 24px", minWidth: 0 }} data-testid="settings-team">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div>
                  <h2 style={{ fontSize: 16, fontWeight: 650, marginBottom: 2 }}>Team Members</h2>
                  <p style={{ fontSize: 11, color: "var(--text-tertiary)" }} data-testid="team-count-line">{teamShown.length} {teamTab} member{teamShown.length === 1 ? "" : "s"}</p>
                </div>
                <button className="btn btn-primary" style={{ fontSize: 12, padding: "7px 14px" }} onClick={() => setShowInvite(true)} data-testid="invite-member-btn">
                  <Plus style={{ width: 12, height: 12 }} /> Invite member
                </button>
              </div>
              <div style={{ display: "flex", gap: 4, marginBottom: 12, borderBottom: "0.5px solid var(--border)" }} data-testid="team-tabs">
                {[["active", "Active", teamActive.length], ["disabled", "Disabled", teamDisabled.length]].map(([key, label, count]) => (
                  <button
                    key={key}
                    data-testid={`team-tab-${key}`}
                    onClick={() => setTeamTab(key)}
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      padding: "8px 14px", fontSize: 12,
                      fontWeight: teamTab === key ? 650 : 500,
                      color: teamTab === key ? "var(--blue)" : "var(--text-secondary)",
                      borderBottom: teamTab === key ? "2px solid var(--blue)" : "2px solid transparent",
                      marginBottom: -1, fontFamily: "var(--font)",
                    }}
                  >
                    {label} <span style={{ fontSize: 11, opacity: 0.7 }}>{count}</span>
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }} data-testid="team-product-filter-wrap">
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)" }}>Product</label>
                <select
                  data-testid="team-product-filter"
                  value={productFilter}
                  onChange={(e) => setProductFilter(e.target.value)}
                  style={{ fontSize: 11, padding: "4px 8px", borderRadius: 4, border: "0.5px solid var(--border)" }}
                >
                  <option value="">All products</option>
                  {USER_PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
                  <option value="__none__">No product</option>
                </select>
                {productFilter && (
                  <button
                    data-testid="team-product-filter-clear"
                    onClick={() => setProductFilter("")}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--blue)" }}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div style={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }} data-testid="team-members-table-scroll">
              <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: 980 }}>
                <colgroup>
                  <col />
                  <col style={{ width: 116 }} />
                  <col style={{ width: 168 }} />
                  <col style={{ width: 112 }} />
                  <col style={{ width: 64 }} />
                  <col style={{ width: 96 }} />
                  <col style={{ width: 76 }} />
                  <col style={{ width: 84 }} />
                  <col style={{ width: 84 }} />
                </colgroup>
                <thead>
                  <tr style={{ borderBottom: "0.5px solid var(--border)" }}>
                    {["Member", "Role", "Title", "Product", "App access", "Integrations", "Status", "Last active", ""].map(h => (
                      <th key={h} style={{ padding: "8px 10px", fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {teamLoading && (
                    <tr><td colSpan={9} style={{ padding: "20px", textAlign: "center", fontSize: 12, color: "var(--text-tertiary)" }} data-testid="team-members-loading">Loading team members…</td></tr>
                  )}
                  {!teamLoading && teamError && (
                    <tr><td colSpan={9} style={{ padding: "20px", textAlign: "center", fontSize: 12, color: "var(--red)" }} data-testid="team-members-error">{teamError}</td></tr>
                  )}
                  {!teamLoading && !teamError && teamShown.length === 0 && (
                    <tr><td colSpan={9} style={{ padding: "20px", textAlign: "center", fontSize: 12, color: "var(--text-tertiary)" }} data-testid="team-members-empty">{teamTab === "disabled" ? "No disabled accounts — everyone is active." : "No active team members. Click \"Invite member\" above."}</td></tr>
                  )}
                  {!teamLoading && !teamError && teamShown.map(m => {
                    const r = ROLES[m.role] || {};
                    const isEditingRole = roleEditingId === m.id;
                    return (
                      <tr key={m.id} style={{ borderBottom: "0.5px solid var(--border)" }} data-testid={`team-member-${m.id}`}>
                        <td style={{ padding: "10px 10px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: r.pillBg || "var(--gray-light)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: r.pillColor || "var(--gray)" }}>
                              {m.avatar || "?"}
                            </div>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 520 }}>{m.full_name}</div>
                              <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{m.email}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "10px" }}>
                          {isEditingRole ? (
                            <select
                              data-testid={`team-role-select-${m.id}`}
                              value={m.role}
                              onChange={(e) => requestRoleChange(m, e.target.value)}
                              autoFocus
                              style={{ fontSize: 11, padding: "3px 6px", borderRadius: 4, border: "0.5px solid var(--border)", width: "100%", maxWidth: "100%", boxSizing: "border-box" }}
                            >
                              {Object.entries(ROLES).map(([k, rv]) => (
                                <option key={k} value={k}>{rv.label}</option>
                              ))}
                            </select>
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                              <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: r.pillBg, color: r.pillColor, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", verticalAlign: "middle" }}>{r.label}</span>
                              {m.has_custom_permissions && (
                                <span
                                  data-testid={`team-custom-perms-badge-${m.id}`}
                                  title="This user has per-user permission overrides"
                                  style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 4, background: "#FFF8F0", color: "#854F0B", letterSpacing: 0.2 }}
                                >CUSTOM</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "10px" }} data-testid={`team-flag-cell-${m.id}`}>
                          {isEditingRole ? (
                            <select
                              data-testid={`team-flag-select-${m.id}`}
                              value={m.flag || ""}
                              onChange={(e) => handleFlagChange(m, e.target.value)}
                              style={{ fontSize: 11, padding: "3px 6px", borderRadius: 4, border: "0.5px solid var(--border)", width: "100%", maxWidth: "100%", boxSizing: "border-box" }}
                            >
                              <option value="">No title</option>
                              {USER_FLAGS.map(f => (
                                <option key={f} value={f}>{f}</option>
                              ))}
                            </select>
                          ) : (
                            m.flag
                              ? <span data-testid={`team-flag-pill-${m.id}`} style={{ ...FLAG_PILL, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", verticalAlign: "middle" }} title={m.flag}>{m.flag}</span>
                              : <span data-testid={`team-flag-empty-${m.id}`} style={{ fontSize: 11, color: "var(--text-tertiary)" }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "10px" }} data-testid={`team-product-cell-${m.id}`}>
                          {isEditingRole ? (
                            <select
                              data-testid={`team-product-select-${m.id}`}
                              value={m.product || ""}
                              onChange={(e) => handleProductChange(m, e.target.value)}
                              style={{ fontSize: 11, padding: "3px 6px", borderRadius: 4, border: "0.5px solid var(--border)", width: "100%", maxWidth: "100%", boxSizing: "border-box" }}
                            >
                              <option value="">No product</option>
                              {USER_PRODUCTS.map(p => (
                                <option key={p} value={p}>{p}</option>
                              ))}
                            </select>
                          ) : (
                            m.product
                              ? <span data-testid={`team-product-pill-${m.id}`} style={{ ...PRODUCT_PILL, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", verticalAlign: "middle" }} title={m.product}>{m.product}</span>
                              : <span data-testid={`team-product-empty-${m.id}`} style={{ fontSize: 11, color: "var(--text-tertiary)" }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "10px" }}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 3 }}>
                            {(r.apps || ["crm"]).map(app => (
                              <span key={app} style={{ fontSize: 9, fontWeight: 600, padding: "1px 5px", borderRadius: 3, background: "var(--blue-light)", color: "var(--blue)", textTransform: "uppercase" }}>{app}</span>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: "10px" }}>
                          {/* Iter 251 (July 2026) — RC + Outlook connection flags.
                              Green pill = OAuth tokens present + not marked dead.
                              Grey pill = not connected. Hover shows human copy. */}
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }} data-testid={`team-integrations-${m.id}`}>
                            <span
                              data-testid={`team-integration-rc-${m.id}`}
                              data-connected={m.rc_connected ? "1" : "0"}
                              title={m.rc_connected ? "RingCentral connected" : "RingCentral NOT connected — user cannot dial from their own line"}
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 3,
                                fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 3,
                                background: m.rc_connected ? "#ECFDF5" : "#F1F5F9",
                                color:      m.rc_connected ? "#065F46" : "#64748B",
                                border:  m.rc_connected ? "0.5px solid #6EE7B7" : "0.5px solid #E2E8F0",
                                letterSpacing: 0.2,
                              }}
                            >
                              <Phone style={{ width: 9, height: 9 }} /> RC
                              {m.rc_connected ? <Check style={{ width: 9, height: 9 }} /> : <X style={{ width: 9, height: 9 }} />}
                            </span>
                            <span
                              data-testid={`team-integration-m365-${m.id}`}
                              data-connected={m.m365_connected ? "1" : "0"}
                              title={m.m365_connected ? "Microsoft 365 (Outlook) connected" : "Outlook NOT connected — emails and calendar invites will not sync"}
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 3,
                                fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 3,
                                background: m.m365_connected ? "#ECFDF5" : "#F1F5F9",
                                color:      m.m365_connected ? "#065F46" : "#64748B",
                                border:  m.m365_connected ? "0.5px solid #6EE7B7" : "0.5px solid #E2E8F0",
                                letterSpacing: 0.2,
                              }}
                            >
                              <Mail style={{ width: 9, height: 9 }} /> Outlook
                              {m.m365_connected ? <Check style={{ width: 9, height: 9 }} /> : <X style={{ width: 9, height: 9 }} />}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: "10px" }}>
                          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 3, background: m.status === "active" ? "var(--green-light)" : m.status === "invited" ? "var(--amber-light)" : "var(--gray-light)", color: m.status === "active" ? "var(--green)" : m.status === "invited" ? "var(--amber)" : "var(--gray)" }}>{m.status}</span>
                        </td>
                        <td style={{ padding: "10px", fontSize: 11, color: "var(--text-tertiary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.lastActive}</td>
                        <td style={{ padding: "10px" }}>
                          <div style={{ display: "flex", gap: 4 }}>
                            <button
                              data-testid={`team-edit-role-${m.id}`}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 3 }}
                              title="Edit role"
                              onClick={() => setRoleEditingId(isEditingRole ? null : m.id)}
                            >
                              <Edit3 style={{ width: 12, height: 12 }} />
                            </button>
                            {role === "super_admin" && (
                              <button
                                data-testid={`team-edit-perms-${m.id}`}
                                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 3 }}
                                title="Advanced permissions"
                                onClick={() => setPermEditUser(m)}
                              >
                                <Shield style={{ width: 12, height: 12 }} />
                              </button>
                            )}
                            <button
                              data-testid={`team-toggle-status-${m.id}`}
                              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 3 }}
                              title={m.status === "disabled" ? "Re-enable" : "Disable"}
                              onClick={() => handleToggleStatus(m)}
                            >
                              <LogOut style={{ width: 12, height: 12 }} />
                            </button>
                            {(role === "super_admin" || role === "crm_admin") && (
                              <button
                                data-testid={`team-issue-reset-url-${m.id}`}
                                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)", padding: 3 }}
                                title="Issue password reset URL + clear lockout"
                                onClick={() => setResetUrlUser(m)}
                              >
                                <KeyRound style={{ width: 12, height: 12 }} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Roles & Permissions */}
          {activeSection === "roles" && canPerform("settings.team.view", role) && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-roles">
              <RbacMatrix />
            </div>
          )}

          {/* AI Targeting · Weights — DB-tunable scorer + cohort sync */}
          {activeSection === "ai_targeting" && (role === "super_admin" || role === "crm_admin") && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-ai-targeting">
              <ScorerConfigEditor />
            </div>
          )}

          {activeSection === "pipeline_settings" && (role === "super_admin" || role === "crm_admin") && (
            <div className="card" style={{ padding: "20px 24px" }}>
              <PipelineSettingsPanel token={token} />
            </div>
          )}

          {activeSection === "ehi-doc-categories" && role === "super_admin" && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-ehi-doc-categories">
              <EhiDocCategoriesPanel token={token} />
            </div>
          )}
          {activeSection === "field-options" && role === "super_admin" && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-field-options">
              <FieldOptionsPanel token={token} />
            </div>
          )}
          {activeSection === "field-options" && role !== "super_admin" && (
            <div className="card" style={{ padding: "20px 24px", fontSize: 13, color: "#777" }} data-testid="settings-field-options-no-access">
              Field Options can only be managed by a super admin.
            </div>
          )}

          {/* Automations — Sunset Outreach toggle removed 2026-05-22 after
              the loop was deleted wholesale. Section kept as a placeholder
              for future automation toggles; renders an "empty" hint until
              another automation is wired here. */}
          {activeSection === "automations" && role === "super_admin" && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-automations">
              <div style={{ fontSize: 13, color: "var(--text-tertiary)" }}>
                No background automations are currently configured.
              </div>
            </div>
          )}

          {activeSection === "ai_summary_pipelines" && role === "super_admin" && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-ai-summary-pipelines">
              <AISummaryPipelinesPanel />
            </div>
          )}

          {/* Issue Reports — admins see the full triage queue with
              comment authoring; everyone else sees a lightweight "My
              Reports" view of just their own submissions, with any
              support comments rendered inline. Iter 338 (Feb 2026). */}
          {activeSection === "issue_reports" && (
            <div className="card" style={{ padding: "20px 24px" }}>
              {["super_admin", "crm_admin"].includes(role)
                ? <IssueReportsAdmin />
                : <IssueReportsMine />}
            </div>
          )}

          {/* Activity Log */}
          {activeSection === "activity" && canPerform("settings.team.view", role) && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-activity-log">
              <h2 style={{ fontSize: 16, fontWeight: 650, marginBottom: 16 }}>Activity Log</h2>
              <ActivityLogViewer />
            </div>
          )}

          {/* Teams management (P1) */}
          {activeSection === "teams" && (role === "super_admin" || role === "crm_admin" || role === "sales_manager") && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-teams">
              <TeamManagement role={role} users={teamMembers || []} />
            </div>
          )}

          {/* My Signature (Phase 1) */}
          {activeSection === "my-signature" && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-my-signature">
              <MySignature />
            </div>
          )}

          {/* Signature Manager (BW-06 Phase 2 — super_admin only) */}
          {activeSection === "signature-manager" && role === "super_admin" && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-signature-manager">
              <SignatureManager />
            </div>
          )}

          {/* Security & Audit (Phase E) */}
          {activeSection === "audit" && (role === "super_admin" || role === "crm_admin") && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-audit">
              <AuditLogViewer role={role} />
            </div>
          )}

          {/* Integrations */}
          {activeSection === "integrations" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <RCIntegrationPanel token={token} role={role} />
            </div>
          )}

          {/* Billing */}
          {activeSection === "billing" && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-billing">
              <h2 style={{ fontSize: 16, fontWeight: 650, marginBottom: 16 }}>Billing & Plan</h2>
              <div style={{ padding: 16, background: "var(--blue-light)", borderRadius: 8, border: "0.5px solid rgba(0,113,227,0.15)" }}>
                <div style={{ fontSize: 14, fontWeight: 620, color: "var(--blue)" }}>Enterprise Plan</div>
                <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 4 }}>Unlimited users, all features, priority support</div>
              </div>
            </div>
          )}

          {/* Data */}
          {activeSection === "data" && (
            <div className="card" style={{ padding: "20px 24px" }} data-testid="settings-data">
              <h2 style={{ fontSize: 16, fontWeight: 650, marginBottom: 16 }}>Data & Storage</h2>
              <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>Storage used: 24.8 MB of 5 GB</div>
              <div style={{ height: 6, background: "var(--gray-light)", borderRadius: 3, marginTop: 8, overflow: "hidden" }}>
                <div style={{ height: "100%", width: "0.5%", background: "var(--blue)", borderRadius: 3 }} />
              </div>

              {/* Iter 139.4 (ioannis #2b7df372 + #bae1e114 2026-05-26):
                  Admin tools for cleaning up Ampliz-imported dupes and
                  retro-stamping last_contact_date on leads that were
                  contacted before the iter-139.2 backend stamping fix
                  shipped. */}
              <DataAdminTools token={token} />
            </div>
          )}

          {/* Admin Ops — super_admin only (each sub-section handled by AdminOps) */}
          {activeSection.startsWith("ops_") && (
            <AdminOps subSection={activeSection} />
          )}

          {/* Training admin panel (super_admin / crm_admin) */}
          {activeSection === "training" && (
            <TrainingAdminPanel />
          )}

          {/* Sample Pipeline (admin walkthrough seed) */}
          {activeSection === "sample_data" && (
            <SamplePipelinePanel token={token} />
          )}
        </div>
      </div>

      {/* Invite Modal */}
      {showInvite && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowInvite(false)}>
          <div style={{ background: "var(--surface)", borderRadius: 12, width: 540, maxHeight: "90vh", overflow: "auto", padding: "24px 28px" }} onClick={e => e.stopPropagation()} data-testid="invite-modal">
            <h2 style={{ fontSize: 16, fontWeight: 650, marginBottom: 16 }}>Invite team member</h2>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Name</label>
              <input className="input" placeholder="Full name" value={inviteData.name} onChange={e => setInviteData({...inviteData, name: e.target.value})} data-testid="invite-name" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Email</label>
              <input className="input" type="email" placeholder="email@company.com" value={inviteData.email} onChange={e => setInviteData({...inviteData, email: e.target.value})} data-testid="invite-email" />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Role</label>
              <select className="input" value={inviteData.role} onChange={e => setInviteData({...inviteData, role: e.target.value})} data-testid="invite-role">
                {Object.entries(ROLES).map(([k, r]) => <option key={k} value={k}>{r.label}</option>)}
              </select>
              <p style={{ fontSize: 10, color: "var(--text-tertiary)", marginTop: 4 }}>{ROLES[inviteData.role]?.description}</p>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Title <span style={{ fontWeight: 400, color: "var(--text-tertiary)" }}>(optional — functional label, no permissions)</span></label>
              <select className="input" value={inviteData.flag} onChange={e => setInviteData({...inviteData, flag: e.target.value})} data-testid="invite-flag">
                <option value="">No title</option>
                {USER_FLAGS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 4 }}>Product <span style={{ fontWeight: 400, color: "var(--text-tertiary)" }}>(optional)</span></label>
              <select className="input" value={inviteData.product} onChange={e => setInviteData({...inviteData, product: e.target.value})} data-testid="invite-product">
                <option value="">No product</option>
                {USER_PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }} data-testid="invite-role-preview-wrap">
              <RolePreview role={inviteData.role} />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button className="btn" onClick={() => setShowInvite(false)} style={{ fontSize: 12 }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleInvite} disabled={inviteBusy} style={{ fontSize: 12, opacity: inviteBusy ? 0.6 : 1 }} data-testid="send-invite">{inviteBusy ? "Inviting…" : "Send invite"}</button>
            </div>
          </div>
        </div>
      )}
      {pendingRoleChange && (
        <div data-testid="role-change-confirm" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 110, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => { setPendingRoleChange(null); setRoleEditingId(null); }}>
          <div style={{ background: "var(--surface)", borderRadius: 12, width: 560, maxHeight: "90vh", overflow: "auto", padding: "24px 28px" }} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 15, fontWeight: 650, marginBottom: 4 }}>Change role for {pendingRoleChange.member.full_name}?</h2>
            <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginBottom: 14 }}>
              Moving from <strong>{pendingRoleChange.member.role}</strong> → <strong>{pendingRoleChange.newRole}</strong>. Review the capability diff below.
            </div>
            <RolePreview role={pendingRoleChange.newRole} priorRole={pendingRoleChange.member.role} />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button className="btn" onClick={() => { setPendingRoleChange(null); setRoleEditingId(null); }} style={{ fontSize: 12 }} data-testid="role-change-cancel">Cancel</button>
              <button className="btn btn-primary" data-testid="role-change-confirm-btn" onClick={() => handleRoleChange(pendingRoleChange.member, pendingRoleChange.newRole)} style={{ fontSize: 12 }}>Confirm change</button>
            </div>
          </div>
        </div>
      )}
      {permEditUser && (
        <div data-testid="perm-edit-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 115, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setPermEditUser(null)}>
          <div style={{ background: "var(--surface)", borderRadius: 12, width: 640, maxHeight: "90vh", overflow: "auto", padding: "22px 26px" }} onClick={e => e.stopPropagation()}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 650 }}>{permEditUser.full_name || permEditUser.email}</div>
              <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{permEditUser.email} · Role: <strong>{permEditUser.role}</strong></div>
            </div>
            <UserPermissionEditor
              user={permEditUser}
              callerRole={role}
              onClose={() => setPermEditUser(null)}
              onSaved={async () => { await fetchTeamMembers(); }}
            />
          </div>
        </div>
      )}

      {/* Iter 165 — Admin "Issue password reset URL" modal */}
      {resetUrlUser && (
        <div data-testid="reset-url-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 120, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={() => { setResetUrlUser(null); setResetUrlResult(null); setResetUrlReason(""); }}>
          <div style={{ background: "var(--surface, #fff)", borderRadius: 12, width: 560, maxHeight: "90vh", overflow: "auto", padding: "22px 26px", boxShadow: "0 20px 50px rgba(15,23,42,0.25)" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <KeyRound style={{ width: 16, height: 16, color: "#5B6CFF" }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 650, color: "var(--text-primary)" }}>Issue password reset URL</div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{resetUrlUser.email}{resetUrlUser.full_name ? ` · ${resetUrlUser.full_name}` : ""}</div>
                </div>
              </div>
              <button onClick={() => { setResetUrlUser(null); setResetUrlResult(null); setResetUrlReason(""); }} aria-label="Close" style={{ background: "transparent", border: 0, padding: 4, cursor: "pointer", color: "var(--text-tertiary)" }}>
                <X style={{ width: 14, height: 14 }} />
              </button>
            </div>

            {!resetUrlResult && (
              <>
                <p style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.55, marginBottom: 12 }}>
                  Generates a one-shot 60-minute reset URL you can hand to the user via secure channel. Their old password keeps working until they complete the reset. All actions are audit-logged.
                </p>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Reason (audit trail)</label>
                  <input
                    type="text"
                    value={resetUrlReason}
                    onChange={e => setResetUrlReason(e.target.value.slice(0, 200))}
                    placeholder={`e.g. "Locked out — couldn't receive email"`}
                    data-testid="reset-url-reason"
                    style={{ width: "100%", padding: "7px 10px", border: "0.5px solid var(--border, #cbd5e1)", borderRadius: 6, fontSize: 12, fontFamily: "inherit" }}
                  />
                </div>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-secondary)", cursor: "pointer", marginBottom: 16 }}>
                  <input type="checkbox" checked={resetUrlClearLockout} onChange={e => setResetUrlClearLockout(e.target.checked)} data-testid="reset-url-clear-lockout" />
                  Also clear active lockout (recommended)
                </label>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button onClick={() => { setResetUrlUser(null); setResetUrlReason(""); }} style={{ background: "transparent", border: "0.5px solid var(--border, #cbd5e1)", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                  <button
                    data-testid="reset-url-issue-btn"
                    disabled={resetUrlBusy}
                    onClick={async () => {
                      setResetUrlBusy(true);
                      try {
                        const r = await axios.post(
                          `${API}/admin/users/${resetUrlUser.id}/password-reset/issue-url`,
                          { reason: resetUrlReason || null, clear_lockout: resetUrlClearLockout },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        setResetUrlResult(r.data);
                      } catch (e) {
                        toast.error(`Issue failed: ${e?.response?.data?.detail || e.message}`);
                      } finally {
                        setResetUrlBusy(false);
                      }
                    }}
                    style={{ background: "linear-gradient(135deg, #5B6CFF 0%, #4F46E5 100%)", color: "#fff", border: 0, borderRadius: 6, padding: "7px 18px", fontSize: 12, fontWeight: 600, cursor: resetUrlBusy ? "wait" : "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6 }}
                  >
                    {resetUrlBusy ? <RefreshCw style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} /> : <KeyRound style={{ width: 12, height: 12 }} />}
                    {resetUrlBusy ? "Issuing…" : "Issue reset URL"}
                  </button>
                </div>
              </>
            )}

            {resetUrlResult && (
              <div data-testid="reset-url-result">
                <div style={{ background: "#ecfdf5", border: "0.5px solid #a7f3d0", borderRadius: 8, padding: 12, marginBottom: 14, display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <Check style={{ width: 14, height: 14, color: "#059669", marginTop: 2 }} />
                  <div style={{ fontSize: 12, color: "#065f46", lineHeight: 1.5 }}>
                    URL issued for <strong>{resetUrlResult.email}</strong>.{" "}
                    {resetUrlResult.lockout_cleared && "Lockout was cleared. "}
                    Valid until <strong>{new Date(resetUrlResult.expires_at).toLocaleString()}</strong>.
                  </div>
                </div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 4 }}>Reset URL — copy & send via secure channel</label>
                <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
                  <input
                    type="text"
                    value={resetUrlResult.reset_url}
                    readOnly
                    data-testid="reset-url-output"
                    onFocus={e => e.target.select()}
                    style={{ flex: 1, padding: "7px 10px", border: "0.5px solid var(--border, #cbd5e1)", borderRadius: 6, fontSize: 11, fontFamily: "Menlo, Consolas, monospace", background: "var(--surface, #fff)" }}
                  />
                  <button
                    data-testid="reset-url-copy"
                    onClick={() => {
                      navigator.clipboard.writeText(resetUrlResult.reset_url);
                      toast.success("Reset URL copied to clipboard");
                    }}
                    style={{ background: "var(--blue, #2563eb)", color: "#fff", border: 0, borderRadius: 6, padding: "7px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 5 }}
                  >
                    <Copy style={{ width: 11, height: 11 }} /> Copy
                  </button>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <button onClick={() => { setResetUrlUser(null); setResetUrlResult(null); setResetUrlReason(""); }} style={{ background: "var(--blue, #2563eb)", color: "#fff", border: 0, borderRadius: 6, padding: "7px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Done</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
